import type { FlexProps } from '@chakra-ui/react';
import { Flex, Image } from '@chakra-ui/react';
import { features } from '@ui/components/Features';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import useVnoBasicInfo from '@ui/hooks/useVnoBasicInfo';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { RESERVOIR_REWARD_TOKEN_DICT } from '@ui/views/Vno/constants';
import type { VaultType } from '@ui/views/Vno/ReservoirAndFountain/types';
import { currentWallet } from '@veno-app/wallet';

import { GardenVaultCard } from './GardenVaultCard';

export type GardenVaultSelectionProps = Omit<FlexProps, 'onChange'> & {
  vaultType: VaultType | undefined | null;
  onChange: (vaultType: VaultType) => void;
};

const showReservoirCardOnZksync = isFeatureEnabled(
  features.reservoirLockZksync,
);

export const GardenVaultSelection = ({
  onChange,
  vaultType,
  ...props
}: GardenVaultSelectionProps) => {
  const t = useTranslations();
  const sdk = useVenoSDK();

  const showReservoirCard =
    showReservoirCardOnZksync || !currentWallet.useIsZksyncChainId();

  const { isLoading: isReservoirLoading, apr12month: reservoirApr12Month } =
    useVnoBasicInfo('RESERVOIR');
  const { isLoading: isFountainLoading, apr12month: fountainApr12Month } =
    useVnoBasicInfo('FOUNTAIN');

  return (
    <Flex gap="16px" {...props}>
      {showReservoirCard && (
        <GardenVaultCard
          title="Reservoir"
          subtitle={t('Earn {coin}', {
            coin: RESERVOIR_REWARD_TOKEN_DICT[sdk.chainType].name,
          })}
          apr={
            !isReservoirLoading && `${(reservoirApr12Month * 100).toFixed(2)}%`
          }
          icon={
            <Image w="24px" h="24px" src="/tokens/cro.svg" alt="cro icon" />
          }
          isActive={vaultType === 'RESERVOIR'}
          cursor="pointer"
          onClick={() => onChange('RESERVOIR')}
        />
      )}
      <GardenVaultCard
        w="0"
        flex="1"
        title="Fountain"
        subtitle={t('Earn {coin}', {
          coin: 'VNO',
        })}
        apr={!isFountainLoading && `${(fountainApr12Month * 100).toFixed(2)}%`}
        icon={<Image w="24px" h="24px" src="/tokens/vno.svg" alt="vno icon" />}
        isActive={vaultType === 'FOUNTAIN'}
        cursor="pointer"
        onClick={() => onChange('FOUNTAIN')}
      />
    </Flex>
  );
};
